/**
 * Apply values to a container of dangerouslySetInnerHTML-injected fields.
 * Caller should provide the container element appropriate to their component.
 */
export function applyValuesToContainer(
  container: Element | null,
  values: Record<string, string | number | boolean | null | undefined>,
  isAcroFile: boolean = false,
  pairs?: Array<{ key: string; value: string | number | boolean; sequence?: string | number }>
) {
  if (!container) return;
  const root = container as HTMLElement;
  const toBool = (v: unknown) => v === true || v === "true" || v === 1 || v === "1" || v === "yes" || v === "on";

  // First, apply sequence-aware checkbox pairs if provided
  if (pairs && pairs.length > 0) {
    for (const { key, value, sequence } of pairs) {
      if (sequence !== undefined && sequence !== null && String(sequence).length > 0) {
        const selDirect = `input[type="checkbox"][data-id="${CSS.escape(String(key))}"][data-order="${CSS.escape(String(sequence))}"]`;
        const selNested = `[data-id="${CSS.escape(String(key))}"] input[type="checkbox"][data-order="${CSS.escape(String(sequence))}"]`;
        const cb = (root.querySelector(selDirect) || root.querySelector(selNested)) as HTMLInputElement | null;
        if (cb && cb.type === "checkbox") {
          cb.checked = toBool(value);
          cb.dispatchEvent(new Event("input", { bubbles: true }));
          cb.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
    }
  }

  Object.entries(values).forEach(([key, val]) => {
    let byDataId;
    if (!isAcroFile) {
      byDataId = root.querySelector(
        `[data-id="${CSS.escape(String(key))}"]`
      ) as HTMLElement | null;
    } else {
      byDataId = root.querySelector(
        `[data-field-id="${CSS.escape(String(key))}"]`
      ) as HTMLElement | null;
    }
    const byName = root.querySelector(`[name="${key}"]`) as HTMLElement | null;
    const byId = root.querySelector(`#${CSS.escape(String(key))}`) as HTMLElement | null;
    const el = (byDataId || byName || byId) as
      | HTMLInputElement
      | HTMLTextAreaElement
      | HTMLSelectElement
      | null;
    if (!el) return;

    if (el instanceof HTMLInputElement) {
      if (el.type === "checkbox" || el.type === "radio") {
        el.checked = toBool(val);
      } else if (el.type !== "file") {
        el.value = val != null ? String(val) : "";
      }
    } else if (el instanceof HTMLSelectElement) {
      const stringValue = val != null ? String(val) : "";
      el.value = stringValue;
      const option = Array.from(el.options).find((opt) => opt.value === stringValue);
      if (option) option.selected = true;
    } else if (el instanceof HTMLTextAreaElement) {
      el.value = val != null ? String(val) : "";
    }

    el.dispatchEvent(new Event("input", { bubbles: true }));
    el.dispatchEvent(new Event("change", { bubbles: true }));
  });
}

/**
 * Apply array of key/value pairs to a container, supporting checkbox instances
 * differentiated by `data-order` (sequence).
 * - For checkboxes: when `sequence` is provided, we target the specific checkbox
 *   matching both `[data-id]` and `[data-order]` (or an ancestor with `[data-id]`).
 * - For non-checkbox inputs: behaves similar to applyValuesToContainer.
 */
export function applyPairsToContainer(
  container: Element | null,
  pairs: Array<{ key: string; value: string | number | boolean; sequence?: string }>,
  isAcroFile: boolean = false
) {
  if (!container) return;
  const root = container as HTMLElement;
  const toBool = (v: unknown) => v === true || v === "true" || v === 1 || v === "1" || v === "yes" || v === "on";

  for (const { key, value, sequence } of pairs) {
    // If sequence provided, attempt checkbox targeting first
    if (sequence !== undefined && sequence !== null && String(sequence).length > 0) {
      const selDirect = `input[type="checkbox"][data-id="${CSS.escape(String(key))}"][data-order="${CSS.escape(String(sequence))}"]`;
      const selNested = `[data-id="${CSS.escape(String(key))}"] input[type="checkbox"][data-order="${CSS.escape(String(sequence))}"]`;
      const cb = (root.querySelector(selDirect) || root.querySelector(selNested)) as HTMLInputElement | null;
      if (cb && cb.type === "checkbox") {
        cb.checked = toBool(value);
        cb.dispatchEvent(new Event("input", { bubbles: true }));
        cb.dispatchEvent(new Event("change", { bubbles: true }));
        continue;
      }
      // If specifically targeting a sequenced checkbox fails, do not fallback silently.
      // Proceed to generic handling below as a best-effort.
    }

    // Generic handling by data-id/name/id (single element per key)
    let byDataId: HTMLElement | null = null;
    if (!isAcroFile) {
      byDataId = root.querySelector(`[data-id="${CSS.escape(String(key))}"]`) as HTMLElement | null;
    } else {
      byDataId = root.querySelector(`[data-field-id="${CSS.escape(String(key))}"]`) as HTMLElement | null;
    }
    const byName = root.querySelector(`[name="${key}"]`) as HTMLElement | null;
    const byId = root.querySelector(`#${CSS.escape(String(key))}`) as HTMLElement | null;
    const el = (byDataId || byName || byId) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
    if (!el) continue;

    if (el instanceof HTMLInputElement) {
      if (el.type === "checkbox" || el.type === "radio") {
        el.checked = toBool(value);
      } else if (el.type !== "file") {
        el.value = value != null ? String(value) : "";
      }
    } else if (el instanceof HTMLSelectElement) {
      const stringValue = value != null ? String(value) : "";
      el.value = stringValue;
      const option = Array.from(el.options).find((opt) => opt.value === stringValue);
      if (option) option.selected = true;
    } else if (el instanceof HTMLTextAreaElement) {
      el.value = value != null ? String(value) : "";
    }

    el.dispatchEvent(new Event("input", { bubbles: true }));
    el.dispatchEvent(new Event("change", { bubbles: true }));
  }
}

